/**
 * Hook to sync GPT model selection with database
 */

import { useMemo } from "react"

import type { GPTModelItem } from "@/api/models"
import type { SessionType } from "@/types"

import { useAvailableGPTModels } from "./use-config-query"
import { useGeneralSettings } from "./use-module-settings"

export function useGPTModelSettings(sessionType: SessionType) {
  const { settings, updateSettings, isLoading: settingsLoading } = useGeneralSettings()
  const { models: availableModels, isLoading: modelsLoading } = useAvailableGPTModels(sessionType)
  const modelSettingsHydrated = settings !== undefined

  const savedModelId = settings?.gptModel || "auto"
  const shouldStream = settings?.shouldStream ?? true
  const suggestFollowupQuestions = settings?.suggestFollowupQuestions ?? false

  // Find the full model object from available models
  const currentGPTModel = useMemo(() => {
    const model = availableModels.find((m) => m.id === savedModelId)
    if (model) return model

    // Fallback to first available model
    return availableModels[0] || null
  }, [availableModels, savedModelId])

  const updateGPTModel = (model: GPTModelItem) => {
    return updateSettings({ gptModel: model.id })
  }

  const updateStreamSetting = (shouldStream: boolean) => {
    void updateSettings({ shouldStream })
  }

  const updateFollowupQuestions = (suggest: boolean) => {
    void updateSettings({ suggestFollowupQuestions: suggest })
  }

  return {
    gptModel: currentGPTModel,
    availableModels,
    shouldStream,
    suggestFollowupQuestions,
    updateGPTModel,
    updateStreamSetting,
    updateFollowupQuestions,
    modelSettingsHydrated,
    isLoading: settingsLoading || modelsLoading,
  }
}
